import { useState, useRef, useEffect } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';

const categoryImages: Record<string, string> = {
  'Shoes': 'https://readdy.ai/api/search-image?query=Minimal%20sneaker%20silhouette%20on%20clean%20cream%20background%2C%20soft%20lighting%2C%20simple%20product%20photography&width=100&height=100&seq=mob-cat-shoes&orientation=squarish',
  'Hoodies / Sweaters': 'https://readdy.ai/api/search-image?query=Soft%20hoodie%20fabric%20folded%20on%20cream%20background%2C%20minimal%20style%2C%20warm%20lighting&width=100&height=100&seq=mob-cat-hoodies&orientation=squarish',
  'T-Shirts': 'https://readdy.ai/api/search-image?query=Folded%20cotton%20t-shirt%20on%20minimal%20cream%20surface%2C%20soft%20lighting%2C%20editorial%20style&width=100&height=100&seq=mob-cat-tshirts&orientation=squarish',
  'Jackets': 'https://readdy.ai/api/search-image?query=Elegant%20jacket%20detail%20on%20minimal%20cream%20background%2C%20soft%20lighting&width=100&height=100&seq=mob-cat-jackets&orientation=squarish',
  'Pants / Shorts': 'https://readdy.ai/api/search-image?query=Folded%20linen%20fabric%20on%20minimal%20cream%20surface%2C%20soft%20lighting&width=100&height=100&seq=mob-cat-pants&orientation=squarish',
  'Headwear': 'https://readdy.ai/api/search-image?query=Minimal%20cap%20on%20clean%20cream%20background%2C%20soft%20lighting%2C%20simple%20photography&width=100&height=100&seq=mob-cat-headwear&orientation=squarish',
  'Sets': 'https://readdy.ai/api/search-image?query=Clothing%20set%20on%20minimal%20cream%20surface%2C%20soft%20lighting%2C%20editorial%20style&width=100&height=100&seq=mob-cat-sets&orientation=squarish',
  'Bags': 'https://readdy.ai/api/search-image?query=Leather%20bag%20detail%20on%20minimal%20cream%20background%2C%20soft%20lighting&width=100&height=100&seq=mob-cat-bags&orientation=squarish',
  'Accessories': 'https://readdy.ai/api/search-image?query=Minimal%20watch%20on%20cream%20surface%2C%20soft%20lighting%2C%20product%20photography&width=100&height=100&seq=mob-cat-accessories&orientation=squarish',
};

export default function Header() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(event.target as Node)) {
        setGuideDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background-50/90 backdrop-blur-md border-b border-background-200/70">
      <nav className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-2 group cursor-pointer">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-grid-line text-background-50 text-lg"></i>
            </div>
            <span className="text-lg font-heading font-semibold text-foreground-950 whitespace-nowrap">
              Sugargoo <span className="text-primary-500">Spreadsheet</span>
            </span>
          </a>

          <div className="hidden md:flex items-center gap-1">
            <a
              href="/"
              className="px-4 py-2 rounded-full text-foreground-700 hover:text-foreground-900 hover:bg-background-100 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              Home
            </a>

            <div ref={guideRef} className="relative">
              <button
                onClick={() => setGuideDropdownOpen(!guideDropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-foreground-700 hover:text-foreground-900 hover:bg-background-100 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                Shopping Guides
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${guideDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {guideDropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-56 bg-background-50 border border-background-200/70 rounded-xl py-2">
                  <a href="/article/what-is-sugargoo-spreadsheet" className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-background-100 transition-colors cursor-pointer">
                    <i className="ri-book-open-line text-accent-600 text-sm"></i>
                    <span className="text-sm text-foreground-800">Complete Beginner Guide</span>
                  </a>
                  <a href="/article/sugargoo-shipping-cost" className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-background-100 transition-colors cursor-pointer">
                    <i className="ri-ship-line text-accent-600 text-sm"></i>
                    <span className="text-sm text-foreground-800">Shipping Cost Guide</span>
                  </a>
                  <a href="/article/sugargoo-coupon-guide" className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-background-100 transition-colors cursor-pointer">
                    <i className="ri-coupon-line text-accent-600 text-sm"></i>
                    <span className="text-sm text-foreground-800">Coupon &amp; Discounts</span>
                  </a>
                </div>
              )}
            </div>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-grid-line text-base"></i>
                Browse Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-80 bg-background-50 border border-background-200/70 rounded-xl py-2 max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                        {categoryImages[cat.name] ? (
                          <img src={categoryImages[cat.name]} alt={cat.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <i className={`${cat.icon} text-foreground-400 text-sm`}></i>
                          </div>
                        )}
                      </div>
                      <span className="text-sm text-foreground-800 whitespace-nowrap">{cat.name}</span>
                      <i className="ri-arrow-right-up-line text-foreground-400 text-xs ml-auto"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 cursor-pointer"
          >
            <i className={`text-foreground-700 text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/70 py-4 pb-6 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="flex items-center gap-2 mb-4">
              <a
                href="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 text-center px-4 py-2.5 rounded-lg bg-background-100 text-foreground-800 text-sm font-medium cursor-pointer"
              >
                <i className="ri-home-line mr-1.5"></i> Home
              </a>
              <a
                href="/article/what-is-sugargoo-spreadsheet"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 text-center px-4 py-2.5 rounded-lg bg-accent-100 text-accent-700 text-sm font-medium cursor-pointer"
              >
                <i className="ri-book-open-line mr-1.5"></i> Guides
              </a>
            </div>

            <div className="mb-4">
              <p className="text-xs text-foreground-500 font-medium px-2 mb-2">Shopping Guides</p>
              <a
                href="/article/what-is-sugargoo-spreadsheet"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-book-open-line text-accent-600 text-sm"></i>
                <span className="text-sm text-foreground-800">Complete Beginner Guide</span>
              </a>
              <a
                href="/article/sugargoo-shipping-cost"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-ship-line text-accent-600 text-sm"></i>
                <span className="text-sm text-foreground-800">Shipping Cost Guide</span>
              </a>
              <a
                href="/article/sugargoo-coupon-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-coupon-line text-accent-600 text-sm"></i>
                <span className="text-sm text-foreground-800">Coupon &amp; Discounts</span>
              </a>
            </div>

            <div>
              <p className="text-xs text-foreground-500 font-medium px-2 mb-2">Browse Categories</p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                      {categoryImages[cat.name] ? (
                        <img src={categoryImages[cat.name]} alt={cat.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className={`${cat.icon} text-foreground-400 text-xs`}></i>
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-foreground-700 leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="block w-full text-center mt-4 px-5 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>
        )}
      </nav>
    </header>
  );
}